import { zodResolver } from "@hookform/resolvers/zod";
import { Sex } from "@ssf/memdb-api";
import PhoneNumber from "awesome-phonenumber";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
export const memberSchema = z.object({
    firstname: z.string().min(1),
    lastname: z.string().min(1),
    birthday: z
        .string()
        .regex(/([12]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01]))/, "invalid_date")
        .refine((birthday) => {
        const birthYear = Number(birthday.substring(0, 4));
        const currentYear = new Date().getFullYear();
        return birthYear <= currentYear - 6;
    }, "invalid_min_age")
        .refine((birthday) => {
        const birthYear = Number(birthday.substring(0, 4));
        const currentYear = new Date().getFullYear();
        return birthYear >= currentYear - 100;
    }, "invalid_max_age"),
    sex: z.nativeEnum(Sex),
    street: z.string().min(1),
    zipcode: z
        .string()
        .regex(/^\d{3}\s*\d{2}$/, "invalid_zipcode")
        .transform((data) => +data.replace(/\s/g, ""))
        .or(z.number().min(10000).max(99999)),
    city: z.string().min(1),
});
export const contactSchema = z.object({
    email: z.string().email("invalid_email"),
    phone: z
        .string()
        .refine((phone) => new PhoneNumber(phone, "SE").isValid(), "invalid_phone"),
});
export const addressSchema = z.object({
    street: z.string().min(1),
    zipcode: z
        .string()
        .regex(/^\d{3}\s*\d{2}$/, "invalid_zipcode")
        .transform((data) => +data.replace(/\s/g, ""))
        .or(z.number().min(10000).max(99999)),
    city: z.string().min(1),
});
export const contactPartialSchema = z.object({
    email: contactSchema.shape.email.or(z.literal("")).optional(),
    phone: contactSchema.shape.phone.or(z.literal("")).optional(),
});
export const passwordSchema = z.object({
    password: z.string().min(6, "invalid_password"),
});
const errorMap = (error) => ({
    message: error.message || error.code,
});
export const useMemberForm = (schema, useFormProps) => {
    const { t } = useTranslation("MemberForm");
    const form = useForm({
        mode: "onTouched",
        resolver: zodResolver(schema, { errorMap }),
        ...useFormProps,
    });
    const { errors } = form.formState;
    const formFieldsProps = {
        label(key) {
            return t(`label.${key}`, "shape" in schema && schema.shape[key]?.isOptional?.()
                ? { context: "optional" }
                : {});
        },
        error(key) {
            const fieldError = errors[key];
            if (!fieldError?.message) {
                return "";
            }
            const i18nKey = `error.${fieldError.message}`;
            const currentYear = new Date().getFullYear();
            const interpolation = {
                invalid_min_age: currentYear - 6,
                invalid_max_age: currentYear - 100,
            };
            const cutoff = interpolation[fieldError.message];
            return t(i18nKey, cutoff !== undefined ? { cutoff } : {});
        },
        enumLabel(key, label) {
            return t(`label.${key}`, { context: label, defaultValue: "" });
        },
    };
    // @ts-ignore: Unfortunately, we can only omit and pick from a schema if we are sure that the key exists in the shape.
    const member = schema.omit({ email: true, phone: true, password: true });
    // @ts-ignore
    const contact = schema.pick({ email: true, phone: true });
    // @ts-ignore
    const password = schema.pick({ password: true });
    return {
        ...form,
        ...formFieldsProps,
        schemas: [member, contact, password].filter((schema) => Object.values(schema.shape).some(Boolean)),
    };
};
//# sourceMappingURL=useMemberForm.js.map